import { Link } from 'react-router-dom';
import { VIEW_SUBSCRIBE } from '../../common/EsConstants';
import { esDayMonYear } from '../../common/EsDate';
import { EsFinanceMeter, esCount, esMoney, useEsFinances } from '../../common/EsFinances';
import { useEsStrings } from '../../common/EsStrings';

const ROW = 'border-b border-rule';
const CELL = 'py-2';
const AMOUNT = `${ CELL } pl-4 text-right tabular-nums whitespace-nowrap`;

const PLACEHOLDER_LINE = ( pCopykey, pDescription, pDetail ) => ( { copykey: pCopykey, description: pDescription, detail: pDetail, monthlyCents: 100000 } );

const PLACEHOLDER = {
    runRateCents: 1000000,
    lines: [
        PLACEHOLDER_LINE( 'servers', 'Servers', 'Production and dev hosting' ),
        PLACEHOLDER_LINE( 'databases', 'Databases', 'Accounts, codex, characters, campaigns, chat' ),
        PLACEHOLDER_LINE( 'network', 'Network', 'Load balancer, web addresses, uptime checks' ),
        PLACEHOLDER_LINE( 'ci-cd', 'CI/CD', 'GitHub and build minutes' ),
        PLACEHOLDER_LINE( 'miscellaneous', 'Miscellaneous', 'Domains, Secrets, Storage, Email fees' ),
    ],
    hostingQuarterFill: 0, hostingQuarterGoal: 10, fullTimeQuarterFill: 0, fullTimeQuarterGoal: 500,
    hostingYearFill: 0, hostingYearGoal: 50, hostingUntil: '2027-01-01', hostingGoal: 5,
    fullTimeYearFill: 0, fullTimeYearGoal: 2000, fullTimeUntil: '2027-01-01', fullTimeGoal: 100,
};

/**
 * Each cost's name and detail in the reader's language, by the row's copykey (V102): each
 * literal t() here is one copykeys/*.json carries. A row's own text stands when an
 * administrator has rewritten a seeded row away from its English,
 * so an edit on the expense form still shows. Miscellaneous's detail is its folds' names.
 */
const COST_TEXT = {
    'servers': ( t ) => [ t( 'home-finances-cost-servers', 'Servers' ), t( 'home-finances-cost-servers-detail', 'Production and dev hosting' ) ],
    'databases': ( t ) => [ t( 'home-finances-cost-databases', 'Databases' ), t( 'home-finances-cost-databases-detail', 'Accounts, codex, characters, campaigns, chat' ) ],
    'network': ( t ) => [ t( 'home-finances-cost-network', 'Network' ), t( 'home-finances-cost-network-detail', 'Load balancer, web addresses, uptime checks' ) ],
    'ci-cd': ( t ) => [ t( 'home-finances-cost-ci-cd', 'CI/CD' ), t( 'home-finances-cost-ci-cd-detail', 'GitHub and build minutes' ) ],
    'domains': ( t ) => [ t( 'home-finances-cost-domains', 'Domains' ), t( 'home-finances-cost-domains-detail', 'escapade.gg and alderloons.com, renewed each July' ) ],
    'secrets': ( t ) => [ t( 'home-finances-cost-secrets', 'Secrets' ), t( 'home-finances-cost-secrets-detail', 'Locked storage for passwords and keys' ) ],
    'storage': ( t ) => [ t( 'home-finances-cost-storage', 'Storage' ), t( 'home-finances-cost-storage-detail', 'File storage' ) ],
    'email-fees': ( t ) => [ t( 'home-finances-cost-email-fees', 'Email fees' ), t( 'home-finances-cost-email-fees-detail', 'The mail the site sends' ) ],
    'miscellaneous': ( t ) => [ t( 'home-finances-cost-miscellaneous', 'Miscellaneous' ), null ],
};

const COST_ENGLISH = Object.fromEntries( Object.entries( COST_TEXT ).map( ( [ pKey, pText ] ) => [ pKey, pText( ( pName, pEnglish ) => pEnglish ) ] ) );

// A copykey an administrator gave a later row (the expense form's Copykey) is looked up by name,
// home-finances-cost-<copykey> and its -detail, its row's own text standing in until the key exists.
function costName( t, pLine ) {
    const aText = COST_TEXT[ pLine.copykey ];
    if ( !aText ) return pLine.copykey ? t( 'home-finances-cost-' + pLine.copykey, pLine.description ) : pLine.description;
    return pLine.description === COST_ENGLISH[ pLine.copykey ][ 0 ] ? aText( t )[ 0 ] : pLine.description;
}

function costDetail( t, pLine ) {
    if ( pLine.folds?.length ) return pLine.folds.map( ( pFold ) => costName( t, pFold ) ).join( t( 'home-finances-cost-separator', ', ' ) );
    const aText = COST_TEXT[ pLine.copykey ];
    if ( !pLine.detail ) return pLine.detail;
    if ( !aText ) return pLine.copykey ? t( 'home-finances-cost-' + pLine.copykey + '-detail', pLine.detail ) : pLine.detail;
    return pLine.detail === COST_ENGLISH[ pLine.copykey ][ 1 ] ? aText( t )[ 1 ] : pLine.detail;
}

/**
 * Finances: every cost as a month of it, and the two goals the subscribers fill in
 * turn: the next quarter's hosting, then its Tim, then the quarter after's, so none is
 * counted twice. Each meter shows its own fill, once for that quarter and once for the
 * year, its sum over four quarters (Tim, 2026-10-11). Every number is esback's, from the expenses an administrator keeps
 * and the subscriptions Stripe has taken; the section says nothing until they arrive.
 *
 * <p>A table of short names and right-aligned figures, and a meter per goal (Tim,
 * 2026-10-11: "more concise, better visuals/formatting"). Each name carries its row's
 * detail as a smaller muted line under it, and the rows are spaced so the table stands
 * about as tall as the meters beside it (Tim, 2026-10-11: "add more detail to monthly
 * costs ... make it as tall as right side"). Name and detail are the expense row's own
 * description and detail (V075), so they stay short in the data; COST_TEXT translates them.
 */
export default function EsHomeFinances() {
    const t = useEsStrings();
    const { summary, error } = useEsFinances();

    return (
        <section id="finances" data-testid="es-home-finances" className="relative z-10 pt-[50px]">
            <div className="container">
                <div className="border-t border-section-rule pt-12">
                    <div className="flex flex-wrap mx-[-16px]">
                        <div className="w-full px-4">
                            <div className="sectionHeading wow fadeInUp" data-wow-delay=".1s">
                                <h2 data-testid="es-home-finances-title" className="sectionTitle">{ t( 'home-finances-title', 'Finances' ) }</h2>
                                <p data-testid="es-home-finances-text" className="bodyText">
                                    { t( 'home-finances-text', 'Escapade is made by one person, Timothy, and paid for by game masters who subscribe. Players are always free.' ) }
                                </p>
                            </div>
                        </div>
                    </div>

                    { error &&
                        <p className="px-4 text-danger" data-testid="es-home-finances-error">
                            { t( 'home-finances-error', 'The numbers could not be read just now.' ) }
                        </p> }

                    { summary && <FinancesBody summary={ summary } testid="es-home-finances" /> }

                    { /* Until the numbers arrive, the same layout over placeholder numbers,
                         invisible, so the section stands at its loaded height from the first
                         paint. It is the only home section whose height waits on a request,
                         and Contact sits under it: a load of /#contact jumped, then the
                         numbers landed and pushed Contact 450px down the screen (Tim,
                         2026-10-14). The placeholder is shaped like production's summary --
                         five costs, each with a detail line -- so the reservation is exact
                         while that holds and off by a row's height per cost added or
                         removed. */ }
                    { !summary && !error &&
                        <div aria-hidden="true" className="invisible" data-testid="es-home-finances-placeholder">
                            <FinancesBody summary={ PLACEHOLDER } testid="es-home-finances-placeholder" />
                        </div> }

                    { /* Signed out too, unlike the nav's: the Subscribe page is public and
                         asks for a sign-in where the paying starts. Outside the summary
                         test, so the placeholder's reserved height already counts it. */ }
                    <div className="flex justify-center pb-10">
                        <Link to={ VIEW_SUBSCRIBE } data-testid="es-home-finances-subscribe-button" className="buttonSubmit">
                            { t( 'nav-menu-subscribe', 'Subscribe' ) }
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FinancesBody( { summary, testid } ) {
    const t = useEsStrings();
    return (
        <div className="-mt-2 flex flex-wrap mx-[-16px] text-base leading-relaxed text-body"
             data-testid={ `${ testid }-body` }>
            { /* lg:pr-10 and lg:pl-10, which replace the row's px-4 on the inner
                 edges: 80px between the table and the meters. Principles' columns
                 sit 50px apart by the box but its prose wraps short of its box,
                 so 80px is what reads the same (Tim, 2026-10-11: "more space
                 between columns, like principles"; measured at 1400). */ }
            <div className="w-full px-4 mb-10 lg:w-1/2 lg:pr-10">
                <h3 className="blurbHeading">{ t( 'home-finances-monthly', 'Monthly costs' ) }</h3>
                { /* -mt-2 takes back the first row's py-2, so the gap under "Monthly costs"
                     is the gap under "Needs for the quarter"; the last row's pb-4 is
                     a little air above the Total's hairline (Tim, 2026-10-11). */ }
                <table className="-mt-2 w-full border-collapse [&_tbody_tr:last-child_td]:pb-4" data-testid={ `${ testid }-costs` }>
                    { /* The head is for screen readers only. Its visible "a month" and
                         the hairline under it went on 2026-10-11 (Tim); the Total row
                         says "a month" for the column. */ }
                    <thead>
                        <tr>
                            <th scope="col" className="sr-only">{ t( 'home-finances-cost', 'Cost' ) }</th>
                            <th scope="col" className="sr-only">{ t( 'home-finances-a-month', 'A month' ) }</th>
                        </tr>
                    </thead>
                    <tbody>
                        { summary.lines.map( ( pLine, pIndex ) => (
                            <tr key={ pIndex } className={ ROW } data-testid={ `${ testid }-cost-line` }>
                                <td className={ CELL }>
                                    { costName( t, pLine ) }
                                    { costDetail( t, pLine ) &&
                                        <span className="block text-xs text-aside"
                                              data-testid={ `${ testid }-cost-detail` }>
                                            { costDetail( t, pLine ) }
                                        </span> }
                                </td>
                                <td className={ AMOUNT }>{ esMoney( pLine.monthlyCents ) }</td>
                            </tr>
                        ) ) }
                    </tbody>
                    <tfoot>
                        <tr className="border-t border-section-rule font-semibold" data-testid={ `${ testid }-total` }>
                            <td className={ CELL }>{ t( 'home-finances-total', 'Total' ) }</td>
                            <td className={ AMOUNT }>{ t( 'home-finances-per-month', '{amount} a month', { amount: esMoney( summary.runRateCents ) } ) }</td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div className="w-full px-4 mb-10 lg:w-1/2 lg:pl-10">
                <h3 className="blurbHeading">{ t( 'home-finances-quarter', 'Needs for the quarter' ) }</h3>
                <EsFinanceMeter data-testid={ `${ testid }-quarter-hosting` }
                             name={ t( 'home-finances-hosting', 'Hosting' ) } months={ 3 }
                             have={ summary.hostingQuarterFill } goal={ summary.hostingQuarterGoal }
                             aside={ t( 'home-finances-more', '{count} more', { count: esCount( summary.hostingQuarterGoal - summary.hostingQuarterFill ) } ) } />
                <EsFinanceMeter data-testid={ `${ testid }-quarter-full-time` }
                             name={ t( 'home-finances-then-timothy', 'Then Timothy' ) } months={ 3 }
                             have={ summary.fullTimeQuarterFill } goal={ summary.fullTimeQuarterGoal }
                             aside={ t( 'home-finances-more', '{count} more', { count: esCount( summary.fullTimeQuarterGoal - summary.fullTimeQuarterFill ) } ) } />

                <h3 className="blurbHeading">{ t( 'home-finances-year', 'Needs for the year' ) }</h3>
                <EsFinanceMeter data-testid={ `${ testid }-hosting` } name={ t( 'home-finances-hosting', 'Hosting' ) }
                             have={ summary.hostingYearFill } goal={ summary.hostingYearGoal }
                             aside={ t( 'home-finances-runs-out', 'runs out {date}', { date: esDayMonYear( summary.hostingUntil ) } ) }
                             rate={ t( 'home-finances-rate', '{count} more, or {monthly} a month', { count: esCount( summary.hostingYearGoal - summary.hostingYearFill ), monthly: esCount( summary.hostingGoal ) } ) } />
                <EsFinanceMeter data-testid={ `${ testid }-full-time` } name={ t( 'home-finances-timothy', 'Timothy' ) }
                             have={ summary.fullTimeYearFill } goal={ summary.fullTimeYearGoal }
                             aside={ t( 'home-finances-runs-out', 'runs out {date}', { date: esDayMonYear( summary.fullTimeUntil ) } ) }
                             rate={ t( 'home-finances-rate', '{count} more, or {monthly} a month', { count: esCount( summary.fullTimeYearGoal - summary.fullTimeYearFill ), monthly: esCount( summary.fullTimeGoal ) } ) } />
            </div>
        </div>
    );
}
